import { useEffect, useState } from "react";
import { KeyRound, Loader2, Trash2, ShieldCheck, Check } from "lucide-react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";

const SCOPE_LABEL = { user: "Ma clé personnelle", org: "Clé de l'organisation" };

function KeyRow({ provider, scope, existing, canManage, onSaved }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const save = async () => {
    if (!value.trim()) return;
    setBusy(true);
    try {
      await api.put("/workspace/llm-keys", { scope, provider: provider.id, key: value.trim() });
      notify.success(`Clé ${provider.label} vérifiée et enregistrée.`);
      setValue(""); setEditing(false); onSaved();
    } catch (e) { notify.error(fmtError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/workspace/llm-keys/${scope}/${provider.id}`);
      notify.success("Clé supprimée."); onSaved();
    } catch (e) { notify.error(fmtError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0"
         data-testid={`llm-key-row-${scope}-${provider.id}`}>
      <div className="w-44 shrink-0 text-xs text-zinc-300">{provider.label}</div>
      {existing && !editing ? (
        <>
          <code className="text-xs text-zinc-400 flex-1">{existing.masked}</code>
          {existing.verified_at && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#1D9E75]">
              <ShieldCheck className="w-3 h-3" /> vérifiée
            </span>
          )}
          {canManage && (
            <>
              <button type="button" onClick={() => setEditing(true)} disabled={busy}
                      className="text-[11px] text-zinc-400 hover:text-white"
                      data-testid={`llm-key-replace-${scope}-${provider.id}`}>Remplacer</button>
              <button type="button" onClick={remove} disabled={busy}
                      className="text-zinc-500 hover:text-[#E24B4A]"
                      data-testid={`llm-key-delete-${scope}-${provider.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </>
      ) : canManage ? (
        <>
          <input type="password" value={value} onChange={(e) => setValue(e.target.value)}
                 placeholder={`Clé API ${provider.label}…`} autoComplete="off"
                 className="flex-1 bg-black/20 border border-white/10 rounded px-2 py-1 text-xs text-zinc-200 focus:border-[#378ADD]/60 outline-none"
                 data-testid={`llm-key-input-${scope}-${provider.id}`} />
          <button type="button" onClick={save} disabled={busy || !value.trim()}
                  className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded bg-[#378ADD]/15 text-[#7cb8ec] border border-[#378ADD]/30 hover:bg-[#378ADD]/25 disabled:opacity-40"
                  data-testid={`llm-key-save-${scope}-${provider.id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Vérifier &amp; enregistrer
          </button>
          {existing && (
            <button type="button" onClick={() => { setEditing(false); setValue(""); }}
                    className="text-[11px] text-zinc-500 hover:text-white">Annuler</button>
          )}
        </>
      ) : (
        <span className="text-xs text-zinc-600 flex-1">— non configurée —</span>
      )}
    </div>
  );
}

export default function LlmKeysCard() {
  const [data, setData] = useState(null);

  const load = () =>
    api.get("/workspace/llm-keys").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const find = (list, p) => (list || []).find((k) => k.provider === p);

  return (
    <section className="noam-card p-6 space-y-4" data-testid="llm-keys-card">
      <header>
        <div className="flex items-center gap-2 text-[10px] font-semibold tracking-[0.18em] uppercase text-[#378ADD]">
          <KeyRound className="w-3 h-3" /> Clés API IA
        </div>
        <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
          Utilisez vos propres clés Claude, ChatGPT ou Gemini : vos requêtes passent alors
          sous <strong className="text-zinc-300">votre</strong> compte fournisseur (données et
          facturation). Priorité : votre clé personnelle → la clé de l'organisation → le moteur NOAM.
          Les clés sont chiffrées et jamais réaffichées en clair.
        </p>
        {data.plan_blocked && (
          <p className="text-[11px] mt-2 px-2.5 py-1.5 rounded bg-[#EF9F27]/10 border border-[#EF9F27]/30 text-[#EF9F27]"
             data-testid="llm-keys-plan-blocked">
            Votre plan « {data.plan} » n'inclut pas le moteur IA de NOAM : une clé API est
            requise pour les fonctions d'intelligence.
          </p>
        )}
      </header>

      {["user", ...(data.can_manage_org ? ["org"] : [])].map((scope) => (
        <div key={scope}>
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
            {SCOPE_LABEL[scope]}
          </div>
          {(data.providers || []).map((p) => (
            <KeyRow key={`${scope}-${p.id}`} provider={p} scope={scope}
                    existing={find(scope === "user" ? data.my_keys : data.org_keys, p.id)}
                    canManage={scope === "user" || data.can_manage_org}
                    onSaved={load} />
          ))}
        </div>
      ))}
      {!data.can_manage_org && (data.org_keys || []).length > 0 && (
        <div className="text-[11px] text-zinc-500">
          Une clé d'organisation est configurée par votre administrateur ; votre clé
          personnelle la remplace si vous en ajoutez une.
        </div>
      )}
    </section>
  );
}
